/**
 * BillingIntervalSelector Component
 *
 * Horizontal scrollable billing interval picker
 * Shows week/month/quarter/semester/year with badges and savings
 *
 * Phase CMS-2: Now accepts pricing data from parent via hook
 * (supports both CMS and constants.ts fallback)
 *
 * @example
 * ```tsx
 * const { pricingByInterval } = useSubscriptionPricing('drama_plus', 'individual');
 *
 * <BillingIntervalSelector
 *   selectedInterval={selectedInterval}
 *   onIntervalChange={setSelectedInterval}
 *   pricingByInterval={pricingByInterval}
 * />
 * ```
 */
import { View, Text, ScrollView, Pressable } from "react-native";
import { useTranslation } from "@dramadetector/shared";
import { formatDisplayPrice, formatBilledAs, formatBadge } from "../../../lib/pricing-i18n";
import type { SubscriptionPrice, PricingByInterval } from "../../../hooks/useSubscriptionPricing";

type BillingInterval = 'week' | 'month' | 'quarter' | 'semester' | 'year';

interface BillingIntervalSelectorProps {
  /** Currently selected billing interval */
  selectedInterval: BillingInterval;
  /** Callback when user selects a different interval */
  onIntervalChange: (interval: BillingInterval) => void;
  /**
   * Pricing data indexed by billing interval.
   * Sourced from CMS when enabled, falls back to constants.ts.
   * Missing intervals are gracefully skipped during rendering.
   *
   * @example { month: { price: 9.99, ... }, year: { price: 99.99, ... } }
   */
  pricingByInterval: PricingByInterval;
}

export function BillingIntervalSelector({
  selectedInterval,
  onIntervalChange,
  pricingByInterval,
}: BillingIntervalSelectorProps) {
  const { t } = useTranslation('subscription');

  // Billing options with translated display names and badges
  const billingOptions = [
    { interval: 'week' as const, displayName: t('intervals.week'), badge: t('badges.new') },
    { interval: 'month' as const, displayName: t('intervals.month') },
    { interval: 'quarter' as const, displayName: t('intervals.quarter'), savings: t('savings.ten_percent') },
    { interval: 'semester' as const, displayName: t('intervals.semester'), savings: t('savings.twenty_percent') },
    { interval: 'year' as const, displayName: t('intervals.year'), savings: t('savings.twenty_five_percent'), badge: formatBadge(t as any, 'best_value') },
  ];

  return (
    <ScrollView
      horizontal
      showsHorizontalScrollIndicator={false}
      className="px-4 mb-6"
      contentContainerStyle={{ gap: 12 }}
    >
      {billingOptions.map((option) => {
        const isSelected = selectedInterval === option.interval;
        const pricing = pricingByInterval[option.interval];

        // Skip rendering if pricing data is missing
        if (!pricing) return null;

        // Map BillingInterval to interval format for display functions
        const intervalFormat = option.interval === 'year' ? 'year' : option.interval;

        return (
          <Pressable
            key={option.interval}
            onPress={() => onIntervalChange(option.interval)}
            className={`border-2 rounded-xl p-4 min-w-[140px] ${
              isSelected ? 'border-purple-600 bg-purple-50' : 'border-gray-200 bg-white'
            }`}
          >
            {/* Show CMS promo badge or static badge/savings */}
            {(pricing.promoBadge || option.badge || option.savings) && (
              <View className="bg-purple-100 rounded-full px-3 py-1 self-start mb-2">
                <Text className="text-purple-700 text-xs font-bold">
                  {pricing.promoBadge || option.badge || option.savings}
                </Text>
              </View>
            )}
            <Text className="text-gray-900 font-semibold text-center mb-1">
              {option.displayName}
            </Text>
            <Text className="text-gray-900 text-xl font-bold text-center">
              {formatDisplayPrice(t as any, pricing.price, intervalFormat)}
            </Text>
            {/* Show billedAs from CMS or calculate from price */}
            {option.interval !== 'month' && (
              <Text className="text-gray-500 text-xs text-center mt-1">
                {pricing.billedAs || formatBilledAs(t as any,
                  option.interval === 'week' ? pricing.price * 4 :
                  option.interval === 'quarter' ? pricing.price * 3 :
                  option.interval === 'semester' ? pricing.price * 6 :
                  pricing.price * 12,
                  intervalFormat
                )}
              </Text>
            )}
          </Pressable>
        );
      })}
    </ScrollView>
  );
}
